import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from '../../../shared/components/Modal';
import Button from '../../../shared/components/Button';
import { Toggle } from '../../../shared/components/Toggle';
import { CurationStatusBadge } from './CurationStatusBadge';
import { Stars } from './Stars';
import type { Review } from '../api/reviewsApi';
import { canFeature } from '../status';
import { useSetReviewStatus, useSetReviewFeatured, useDeleteReview } from '../hooks/useReviews';

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

interface Props {
  review: Review | null;
  isOpen: boolean;
  onClose: () => void;
  canManage: boolean;
}

export const ReviewDetailModal = ({ review, isOpen, onClose, canManage }: Props) => {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const setStatus = useSetReviewStatus();
  const setFeatured = useSetReviewFeatured();
  const deleteReview = useDeleteReview();

  if (!review) return null;

  const handleDelete = () => {
    deleteReview.mutate(review.id, {
      onSuccess: () => {
        setConfirmingDelete(false);
        onClose();
      },
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <div className="p-8 max-h-[85vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold text-foreground">Review</h2>
              <CurationStatusBadge status={review.status} />
            </div>
            <p className="text-[13px] text-muted-foreground mt-1">{formatDate(review.createdAt)}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-muted-foreground text-2xl leading-none">
            &times;
          </button>
        </div>

        <div className="flex flex-col gap-5">
          <Stars rating={review.rating} size={18} />

          {review.title && <h3 className="text-[16px] font-[700] text-foreground">{review.title}</h3>}
          <p className="text-[14px] text-foreground whitespace-pre-wrap">{review.body || '—'}</p>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[14px] font-medium text-foreground">{review.customerName}</span>
            {review.verifiedPurchase && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                Verified purchase
              </span>
            )}
          </div>

          <div>
            <span className="text-[12px] text-muted-foreground block mb-1">Product</span>
            <Link
              to={`/products/edit/${review.productId}`}
              className="text-[14px] text-foreground underline hover:no-underline"
            >
              {review.productId.slice(0, 8)}
            </Link>
          </div>

          {canManage && (
            <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-border">
              <Button
                variant="outline"
                size="sm"
                disabled={review.status === 'APPROVED' || setStatus.isPending}
                onClick={() => setStatus.mutate({ id: review.id, status: 'APPROVED' })}
              >
                Approve
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={review.status === 'REJECTED' || setStatus.isPending}
                onClick={() => setStatus.mutate({ id: review.id, status: 'REJECTED' })}
              >
                Reject
              </Button>
              <Toggle
                label="Featured"
                checked={review.isFeatured}
                onChange={(checked) => setFeatured.mutate({ id: review.id, isFeatured: checked })}
                disabled={!canFeature(review.status)}
                title={canFeature(review.status) ? undefined : 'Approve first to feature'}
              />
              <Button
                variant="transparent"
                size="sm"
                className="ml-auto text-red-600 hover:bg-red-50"
                onClick={() => setConfirmingDelete(true)}
              >
                Delete
              </Button>
            </div>
          )}
        </div>
      </div>

      {confirmingDelete && (
        <Modal isOpen={confirmingDelete} onClose={() => setConfirmingDelete(false)} size="sm">
          <div className="p-6">
            <h3 className="text-[16px] font-[700] text-foreground mb-2">Delete this review?</h3>
            <p className="text-[14px] text-muted-foreground mb-6">This cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" size="sm" onClick={() => setConfirmingDelete(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                className="bg-red-600 hover:bg-red-700"
                loading={deleteReview.isPending}
                onClick={handleDelete}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Modal>
  );
};
